// @mui
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Toolbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
// utils
// components
//
import _ from 'lodash';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LogoButton } from '../../../components/style/ButtonStyle';
import { FormLabelText } from '../../../components/style/TextStyle';
import useResponsive from '../../../hooks/useResponsive';
import { userSelector } from '../../../store/userSlice';
import { UserRoleList } from '../../../utils/enum';
import { DrawerIcon, LogoutIcon } from '../nav/IconButton';
import NavSection from '../nav/NavSection';
import {
  desktopManagerNavConfig,
  desktopNavConfig,
  mobileManagerNavConfig,
  mobileNavConfig,
} from '../nav/config';
import AccountPopover from './AccountPopover';
import { HeaderInfo, PersonIcon } from './HeaderStyle';
import { useLogout } from '../../../hooks/useLogout';
import { ModalText, useModalToggle } from '../../../components/modal/Modal';
import { ApproveModal } from '../../../components/modal/ApproveModal';

// ----------------------------------------------------------------------

const HEADER_MOBILE = 64;

const HEADER_DESKTOP = 74;

const StyledRoot = styled(AppBar)(({ theme }) => ({
  boxShadow: 'none',
  [theme.breakpoints.up('lg')]: {
    width: '100%',
  },
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: HEADER_MOBILE,
  [theme.breakpoints.up('lg')]: {
    minHeight: HEADER_DESKTOP,
    padding: theme.spacing(0, 2),
  },
  background: '#fff',
  [theme.breakpoints.down('md')]: { justifyContent: 'space-between' },
  boxShadow: ' 0px 4px 14px 0px #0000000F',
}));

// ----------------------------------------------------------------------

export default function Header() {
  const mdUp = useResponsive('up', 'md');
  const navigate = useNavigate();
  const user = useSelector(userSelector);
  const isManager = _.isEqual(user?.role, UserRoleList.MANAGER);
  return (
    <Stack direction="column">
      <StyledRoot>
        <StyledToolbar>
          <LogoButton onClick={() => navigate(`/map`)} disableRipple>
            <img
              src={require('../../../app/static/images/itl_logo.png')}
              alt="logo"
            />
          </LogoButton>
          {mdUp ? (
            <DesktopMenuBar isManager={isManager} user={user} />
          ) : (
            <MobileDrawer isManager={isManager} user={user} />
          )}
        </StyledToolbar>
      </StyledRoot>
    </Stack>
  );
}

function DesktopMenuBar({ isManager, user }) {
  return (
    <>
      <Box sx={{ minWidth: '0px', maxWidth: '160px', flex: 1 }} />
      {isManager ? (
        <NavSection navConfig={desktopManagerNavConfig} />
      ) : (
        <NavSection navConfig={desktopNavConfig} />
      )}
      <Box sx={{ flexGrow: 1 }} />
      <Stack
        direction="row"
        alignItems="center"
        spacing={{
          xs: 0.5,
          sm: 1,
        }}
      >
        <HeaderInfo>{user?.name}</HeaderInfo>
        <AccountPopover />
      </Stack>
    </>
  );
}

function MobileDrawer({ isManager, user }) {
  const [openDrawer, setOpenDrawer] = useState(false);
  const { logout } = useLogout();
  const { on, open, close } = useModalToggle();

  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }

      setOpenDrawer(open);
    };

  return (
    <div>
      <IconButton onClick={toggleDrawer(true)}>
        <DrawerIcon />
      </IconButton>
      <Drawer anchor="right" open={openDrawer} onClose={toggleDrawer(false)}>
        <Stack
          role="presentation"
          onClick={() => {
            toggleDrawer(false);
          }}
          onKeyDown={toggleDrawer(false)}
          sx={{ minWidth: 250, p: 2, flex: 1 }}
        >
          <Stack
            sx={{ minHeight: 152 }}
            alignItems="center"
            justifyContent="center"
            spacing={2}
          >
            <PersonIcon />
            <FormLabelText>{user?.name}</FormLabelText>
          </Stack>
          <Box sx={{ pb: 2 }}>
            <Divider />
          </Box>

          {isManager ? (
            <NavSection navConfig={mobileManagerNavConfig} />
          ) : (
            <NavSection navConfig={mobileNavConfig} />
          )}
          <Divider />
        </Stack>
        <Stack justifyContent="center" alignItems="center">
          <Stack sx={{ p: 1, pb: 2 }} direction="row" alignItems="center">
            <Button
              startIcon={<LogoutIcon />}
              onClick={() => {
                toggleDrawer(true);
                open();
              }}
            >
              로그아웃
            </Button>
          </Stack>
          <ApproveModal
            title="로그아웃"
            on={on}
            onClose={close}
            onConfirm={logout}
            isPrimaryColor={false}
          >
            <Stack
              direction="row"
              justifyContent="center"
              sx={{ pt: 3, pb: 5 }}
            >
              <ModalText>로그아웃 하시겠습니까?</ModalText>
            </Stack>
          </ApproveModal>
        </Stack>
      </Drawer>
    </div>
  );
}
